import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import Sidebar from '../components/Sidebar';
import axios from 'axios';
import common from '../utils/common';
import { authStorage } from '../utils/authStorage';
import { releaseService, default as apiClient } from '../services/api';
import { 
  fetchInvestmentDetails, 
  fetchParticipants, 
  fetchCoinGeckoPrices, 
  fetchVestingSchedule 
} from '../features/api/apiSlice';
import './ntc-branding.css';

const InvestorDashboard = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  
  // Redux selectors for API data
  const { investmentDetails: reduxInvestmentDetails, participants: reduxParticipants, coinGeckoData: reduxCoinGeckoData, vestingSchedule: reduxVestingSchedule, loading: apiLoading } = useAppSelector((state) => state.api);
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Investment data state
  const [investmentData, setInvestmentData] = useState({
    amountInvested: 0,
    totalProjects: 0,
    releasedProjects: 0,
    releasedCount: 0,
    unreleasedAmount: 0,
    unreleasedCount: 0,
    overallROI: 0,
    isLoading: true
  });

  // Investment details and filters
  const [investmentDetails, setInvestmentDetails] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [coinGeckoData, setCoinGeckoData] = useState([]);
  const [filters, setFilters] = useState({
    ongoing: true,
    completed: false,
    unreleased: false,
    participantId: null
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showParticipantDropdown, setShowParticipantDropdown] = useState(false);
  const [loadingParticipantData, setLoadingParticipantData] = useState(false);
  const [participantSearchTerm, setParticipantSearchTerm] = useState('');
  
  // Release details state
  const [selectedProject, setSelectedProject] = useState(null);
  const [releaseDetails, setReleaseDetails] = useState([]);
  const [releaseParticipantSellingData, setReleaseParticipantSellingData] = useState([]);
  const [vestingSchedule, setVestingSchedule] = useState([]);
  const [insertRelease, setInsertRelease] = useState(false);
  const [releaseForm, setReleaseForm] = useState({
    release_date: '',
    release_per: '',
    price_at_release: ''
  });
  const [collapseDetail, setCollapseDetail] = useState(false);
  const [editingSellingPrice, setEditingSellingPrice] = useState({});
  const [tempSellingPrice, setTempSellingPrice] = useState({});
  const [isSavingRelease, setIsSavingRelease] = useState(false);

  // Restore sidebar state from localStorage on component mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('nexel-classic-dashboard-menu-mini-theme');
    if (savedTheme === 'menu-mini-theme') {
      setSidebarCollapsed(true);
      document.documentElement.classList.add('minimenu');
    } else {
      setSidebarCollapsed(false);
      document.documentElement.classList.remove('minimenu');
    }
  }, []);

  // Track which user's data we're expecting
  const [expectedUserId, setExpectedUserId] = useState(null);

  // Fetch investment data using Redux
  useEffect(() => {
    const fetchData = async () => {
      try {
        const loginData = authStorage.getLoginData();
        
        if (loginData) {
          const userId = loginData.id;
          setExpectedUserId(userId);
          
          // Clear any existing Redux state first to ensure fresh data
          setInvestmentDetails([]);
          setFilteredProjects([]);
          setInvestmentData(prev => ({ ...prev, isLoading: true }));
          
          // Dispatch Redux thunks to fetch data
          const result = await dispatch(fetchInvestmentDetails(userId));
          dispatch(fetchParticipants());
          dispatch(fetchVestingSchedule());
          
          // Use the result directly instead of waiting for Redux state
          if (fetchInvestmentDetails.fulfilled.match(result)) {
            const investmentData = result.payload;
            setInvestmentDetails(investmentData);
            setFilteredProjects(investmentData);
            processInvestmentData(investmentData);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setInvestmentData(prev => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
  }, [dispatch]);

  // Helper function to process investment data
  const processInvestmentData = (data) => {
    if (!data || data.length === 0) {
      setInvestmentData({
        amountInvested: 0,
        totalProjects: 0,
        releasedProjects: 0,
        releasedCount: 0,
        unreleasedAmount: 0,
        unreleasedCount: 0,
        overallROI: 0,
        isLoading: false
      });
      return;
    }
    
    let amountInvested = 0;
    let releasedProjects = 0;
    let releasedCount = 0;
    let unreleasedAmount = 0;
    let unreleasedCount = 0;
    let overallROI = 0;
    
    data.forEach(inv => {
      amountInvested += Math.round(inv.allocation_amount);
      
      if (inv.status === 'ongoing' || inv.status === 'completed') {
        releasedProjects += Math.round(inv.allocation_amount);
        releasedCount++;
        if (inv.ntc_roi) {
          overallROI += parseFloat(inv.allocation_amount) * (parseFloat(inv.ntc_roi) / 100);
        }
      } else if (inv.status === 'unreleased') {
        unreleasedAmount += Math.round(inv.allocation_amount);
        unreleasedCount++;
      }
    });
    
    setInvestmentData({
      amountInvested,
      totalProjects: data.length,
      releasedProjects,
      releasedCount,
      unreleasedAmount,
      unreleasedCount,
      overallROI: amountInvested > 0 ? (overallROI / amountInvested) * 100 : 0,
      isLoading: false
    });
  };

  // Don't use Redux state updates - we use the fetch result directly
  // This prevents cross-dashboard data leakage from LP dashboard

  // Update participants from Redux
  useEffect(() => {
    if (reduxParticipants) {
      setParticipants(reduxParticipants);
    }
  }, [reduxParticipants]);

  // Update vesting schedule from Redux
  useEffect(() => {
    if (reduxVestingSchedule) {
      setVestingSchedule(reduxVestingSchedule);
    }
  }, [reduxVestingSchedule]);

  // Fetch CoinGecko prices for filtered projects using Redux
  // Named differently so it does not shadow the imported fetchCoinGeckoPrices thunk
  const fetchCoinGeckoPricesFunc = async (projects) => {
    try {
      // Get all coingecko symbols from filtered projects
      const coingeckoSymbols = projects
        .map(inv => inv.coingecko_symbol)
        .filter(symbol => symbol && symbol !== '')
        .join(',');
      
      if (coingeckoSymbols) {
        dispatch(fetchCoinGeckoPrices(coingeckoSymbols));
      }
    } catch (error) {
      console.error('Error fetching CoinGecko data:', error);
    }
  };

  // Update CoinGecko data from Redux
  useEffect(() => {
    if (reduxCoinGeckoData) {
      setCoinGeckoData(reduxCoinGeckoData);
    }
  }, [reduxCoinGeckoData]);

  // Filter projects based on status, participant, and search term
  const filterProjects = async () => {
    let filtered = [...investmentDetails];
    
    // Filter by status
    filtered = filtered.filter(inv => {
      if (filters.ongoing && inv.status === 'ongoing') return true;
      if (filters.completed && inv.status === 'completed') return true;
      if (filters.unreleased && inv.status === 'unreleased') return true;
      return false;
    });
    
    // Note: Participant filtering is handled in handleParticipantChange
    // because we fetch participant-specific data from the API
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(inv => 
        inv.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredProjects(filtered);
    
    // Fetch CoinGecko prices for filtered projects
    await fetchCoinGeckoPricesFunc(filtered);
    
    // Auto-show first project's release details if no project is currently selected
    if (filtered.length > 0 && !selectedProject) {
      showReleaseDetails(filtered[0], false);
    }
  };

  // Update filters when they change
  useEffect(() => {
    filterProjects();
  }, [filters, searchTerm, investmentDetails]);

  // Auto-show first project's release details when filteredProjects changes
  useEffect(() => {
    if (filteredProjects.length > 0 && !selectedProject) {
      showReleaseDetails(filteredProjects[0], false);
    }
  }, [filteredProjects]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilters && !event.target.closest('.position-relative')) {
        setShowFilters(false);
      }
      if (showParticipantDropdown && !event.target.closest('.position-relative')) {
        setShowParticipantDropdown(false);
        setParticipantSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters, showParticipantDropdown]);

  // Handle participant selection
  const handleParticipantChange = async (participantId) => {
    setShowParticipantDropdown(false);
    
    if (participantId) {
      setLoadingParticipantData(true);
      setExpectedUserId(participantId);
      try {
        // Fetch participant-specific investment data using Redux
        const result = await dispatch(fetchInvestmentDetails(participantId));
        
        if (fetchInvestmentDetails.fulfilled.match(result)) {
          const participantInvestmentDetails = result.payload;
          
          // Set the participant-specific data directly from result
          setInvestmentDetails(participantInvestmentDetails);
          processInvestmentData(participantInvestmentDetails);
          
          // Apply current filters to the participant's data
          const filtered = participantInvestmentDetails.filter(inv => {
            // Filter by status
            if (filters.ongoing && inv.status === 'ongoing') return true;
            if (filters.completed && inv.status === 'completed') return true;
            if (filters.unreleased && inv.status === 'unreleased') return true;
            return false;
          });
          
          // Filter by search term
          const searchFiltered = searchTerm ? 
            filtered.filter(inv => inv.name.toLowerCase().includes(searchTerm.toLowerCase())) : 
            filtered;
        
          setFilteredProjects(searchFiltered);
          
          // Auto-show first project's release details
          if (searchFiltered.length > 0) {
            showReleaseDetails(searchFiltered[0], false);
          }
          
          // Update filters
          setFilters(prev => ({ ...prev, participantId }));
        }
      } catch (error) {
        console.error('Error fetching participant investments:', error);
      } finally {
        setLoadingParticipantData(false);
      }
    } else {
      setLoadingParticipantData(true);
      // Reset to current user's investments
      const loginData = authStorage.getLoginData();
      if (loginData) {
        const userId = loginData.id;
        try {
          // Fetch user's investment data using Redux
          const result = await dispatch(fetchInvestmentDetails(userId));
          
          if (fetchInvestmentDetails.fulfilled.match(result)) {
            const userInvestmentDetails = result.payload;
            
            // Set the user's data and update investment data for cards
            setInvestmentDetails(userInvestmentDetails);
            processInvestmentData(userInvestmentDetails);
            
            // Apply current filters to the user's data
            const filtered = userInvestmentDetails.filter(inv => {
              // Filter by status
              if (filters.ongoing && inv.status === 'ongoing') return true;
              if (filters.completed && inv.status === 'completed') return true;
              if (filters.unreleased && inv.status === 'unreleased') return true;
              return false;
            });
            
            // Filter by search term
            const searchFiltered = searchTerm ? 
              filtered.filter(inv => inv.name.toLowerCase().includes(searchTerm.toLowerCase())) : 
              filtered;
          
            setFilteredProjects(searchFiltered);
            
            // Auto-show first project's release details
            if (searchFiltered.length > 0) {
              showReleaseDetails(searchFiltered[0], false);
            }
            
            // Update filters
            setFilters(prev => ({ ...prev, participantId: null }));
          }
        } catch (error) {
          console.error('Error fetching user investments:', error);
        } finally {
          setLoadingParticipantData(false);
        }
      } else {
        setLoadingParticipantData(false);
      }
    }
  };

  // Show release details for selected project
  const showReleaseDetails = async (project, saveReleaseROI = false) => {
    console.log('Showing release details for project:', project);
    setSelectedProject(project);
    
    try {
      const loginData = authStorage.getLoginData();
      
      // Fetch release details for the specific participant
      console.log('Fetching release details for project:', project.proj_id, 'participant:', loginData.id);
      const releaseResponse = await releaseService.getReleaseDetails(`${project.proj_id}/${loginData.id}`);
      
      let releaseData;
      if (typeof releaseResponse === 'string') {
        releaseData = JSON.parse(releaseResponse);
      } else {
        releaseData = releaseResponse;
      }
      
      console.log('Release data:', releaseData);
      
      if (releaseData && releaseData.release_data && releaseData.release_data.length > 0) {
        setReleaseDetails(releaseData.release_data);
        setReleaseParticipantSellingData(releaseData.project_sell_data || []);
        
        // Calculate and save ROI if this is after saving a release
        if (saveReleaseROI) {
          const totalTokens = Math.round(project.allocation_amount) * (1 - parseFloat(project.management_fee) / 100) / parseFloat(project.token_price);
          let totalReleasedTokens = 0;
          let totalUsdt = 0;
          let actualTotalUsdt = 0;
          
          for (let i = 0; i < releaseData.release_data.length; i++) {
            const releasedToken = Math.round(project.allocation_amount) * (1 - parseFloat(project.management_fee) / 100) * parseFloat(releaseData.release_data[i].release_per) / (parseFloat(project.token_price) * 100);
            totalUsdt += releasedToken * parseFloat(releaseData.release_data[i].price_at_release);
            totalReleasedTokens += releasedToken;
            
            const actualSellingPrice = releaseData.project_sell_data?.find(x => x.project_release_id === releaseData.release_data[i].id)?.selling_price;
            if (actualSellingPrice) {
              actualTotalUsdt += releasedToken * parseFloat(actualSellingPrice);
            }
          }
          
          const totalReleasePer = (totalReleasedTokens * 100) / totalTokens;
          const returnOnInvestment = (totalUsdt * 100) / Math.round(project.allocation_amount);
          const actualReturnOnInvestment = (actualTotalUsdt * 100) / Math.round(project.allocation_amount);
          
          // Save ROI
          try {
            await releaseService.saveReleaseROI({
              proj_id: project.proj_id,
              participant_id: loginData.id,
              release_per: totalReleasePer,
              ntc_roi: returnOnInvestment,
              actual_roi: actualReturnOnInvestment
            });
          } catch (roiError) {
            console.error('Error saving ROI:', roiError);
          }
        }
      } else {
        setReleaseDetails([]);
        setReleaseParticipantSellingData([]);
      }
    } catch (error) {
      console.error('Error fetching release details:', error);
      setReleaseDetails([]);
      setReleaseParticipantSellingData([]);
    }
  };

  // Save new release
  const saveRelease = async () => {
    if (!selectedProject) return;
    
    const { release_date, release_per, price_at_release } = releaseForm;
    
    if (!release_date || !release_per || !price_at_release) {
      alert('Please fill all mandatory fields!');
      return;
    }

    setIsSavingRelease(true);
    
    try {
      const loginData = authStorage.getLoginData();
      
      const releaseData = {
        proj_id: selectedProject.proj_id,
        participant_id: loginData.id,
        release_date: release_date,
        release_per: release_per,
        price_at_release: price_at_release,
        comment: '',
        created_by: loginData.id
      };

      console.log('Saving release data:', releaseData);
      
      // Try direct axios call to ensure proper format
      const response = await apiClient.post('/v1/save-release', releaseData);
      console.log('Release saved successfully!', response.data);
      
      setInsertRelease(false);
      setReleaseForm({ release_date: '', release_per: '', price_at_release: '' });
      
      // Refresh release details with ROI calculation
      await showReleaseDetails(selectedProject, true);
      
      alert('Release saved successfully!');
    } catch (error) {
      console.error('Error saving release:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Unknown error';
      
      alert(`Save failed: ${errorMessage}`);
    } finally {
      setIsSavingRelease(false);
    }
  };

  // Save selling price
  const saveSellingPrice = async (projectReleaseId, sellingPrice) => {
    if (!sellingPrice) {
      alert('Please fill selling price!');
      return;
    }

    try {
      const sellingData = {
        project_release_id: projectReleaseId,
        selling_price: sellingPrice
      };

      await releaseService.saveSellingPrice(sellingData);
      
      console.log('Selling price saved successfully!');
      
      // Refresh release details
      if (selectedProject) {
        await showReleaseDetails(selectedProject, true);
      }
    } catch (error) {
      console.error('Error saving selling price:', error);
      alert('Save failed');
    }
  };

  const toggleSidebar = () => {
    const newCollapsedState = !sidebarCollapsed;
    setSidebarCollapsed(newCollapsedState);
    
    // Apply minimenu class to document html element
    if (newCollapsedState) {
      document.documentElement.classList.add('minimenu');
      localStorage.setItem('nexel-classic-dashboard-menu-mini-theme', 'menu-mini-theme');
    } else {
      document.documentElement.classList.remove('minimenu');
      localStorage.setItem('nexel-classic-dashboard-menu-mini-theme', 'menu-expend-theme');
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <div className="nxl-wrapper">
      {/* Navigation Sidebar */}
      <Sidebar 
        sidebarCollapsed={sidebarCollapsed} 
        setSidebarCollapsed={setSidebarCollapsed}
        toggleSidebar={toggleSidebar}
      />

      {/* Main Content */}
      <main className="nxl-container" style={{ paddingTop: '0' }}>
        <div className="nxl-content" style={{ paddingTop: '0' }}>
          {/* Page Header */}
          <div className="page-header">
            <div className="page-header-left d-flex align-items-center">
              {/* Sidebar Toggle Button */}
              <div className="nxl-navigation-toggle me-3">
                <a href="javascript:void(0);" id="menu-mini-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'none' : 'block' }}>
                  <i className="feather-align-left"></i>
                </a>
                <a href="javascript:void(0);" id="menu-expend-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'block' : 'none' }}>
                  <i className="feather-arrow-right"></i>
                </a>
              </div>
              <div className="page-header-title">
                <h5 className="m-b-10">Investor Dashboard</h5>
              </div>
              <ul className="breadcrumb">
                <li className="breadcrumb-item"><a href="/investor-dashboard">Home</a></li>
                <li className="breadcrumb-item">Investor Dashboard</li>
              </ul>
            </div>
            <div className="page-header-right ms-auto d-flex align-items-center gap-3">
              {(() => {
                const userData = authStorage.getLoginData() || {};
                return userData && (
                  <div className="d-flex align-items-center gap-2 d-none d-md-flex">
                    <i className="feather-user text-primary"></i>
                    <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                      Welcome, {(() => {
                        if (participants.length > 0 && userData.id) {
                          const userParticipant = participants.find(p => p.id === userData.id);
                          return userParticipant?.name || userData.name || 'User';
                        }
                        return userData.name || 'User';
                      })()}
                    </span>
                  </div>
                );
              })()}
              <div className="page-header-right-items">
                <div className="d-flex d-md-none">
                  <a href="javascript:void(0)" className="page-header-right-close-toggle">
                    <i className="feather-arrow-left me-2"></i>
                    <span>Back</span>
                  </a>
                </div>
              </div>
              <div className="d-md-none d-flex align-items-center">
                <a href="javascript:void(0)" className="page-header-right-open-toggle">
                  <i className="feather-align-right fs-20"></i>
                </a>
              </div>
            </div>
          </div>

                 {/* Main Content */}
                 <div className="main-content">
                   {/* Dashboard Title */}
                   <div className="mb-4">
                     <h4 className="fw-bold text-dark mb-2">
                       {(() => {
                         const loginData = (authStorage.getLoginData() || {});
                         if (filters.participantId && participants.length > 0) {
                           const participant = participants.find(p => p.id === filters.participantId);
                           return participant ? `${participant.name}'s Dashboard` : `${loginData.name || 'User'}'s Dashboard`;
                         }
                         return `${loginData.name || 'User'}'s Dashboard`;
                       })()}
                     </h4>
                     <p className="text-muted fs-14 mb-0">Track your investment performance</p>
                   </div>
                   
                   <div className="row">
              {/* [Amount Invested] start */}
              <div className="col-xxl-3 col-md-6">
                <div className="card stretch stretch-full">
                  <div className="card-body">
                    <div className="d-flex align-items-start justify-content-between mb-4">
                      <div className="d-flex gap-4 align-items-center">
                        <div className="avatar-text avatar-lg bg-gray-200">
                          <i className="feather-dollar-sign"></i>
                        </div>
                        <div>
                          <div className="fs-4 fw-bold text-dark">
                            ${investmentData.isLoading ? '...' : investmentData.amountInvested.toLocaleString()}
                          </div>
                          <h3 className="fs-13 fw-semibold text-truncate-1-line">Amount Invested</h3>
                        </div>
                      </div>
                      <a href="javascript:void(0);" className="">
                        <i className="feather-more-vertical"></i>
                      </a>
                    </div>
                    <div className="pt-4">
                      <div className="d-flex align-items-center justify-content-between">
                        <a href="javascript:void(0);" className="fs-12 fw-medium text-muted text-truncate-1-line">Total Investment</a>
                        <div className="w-100 text-end">
                          <span className="fs-12 text-dark">{investmentData.totalProjects} Projects</span>
                          <span className="fs-11 text-muted">({investmentData.releasedCount} Released)</span>
                        </div>
                      </div>
                      <div className="progress mt-2 ht-3">
                        <div className="progress-bar bg-primary" role="progressbar" style={{width: `${investmentData.totalProjects > 0 ? (investmentData.releasedCount / investmentData.totalProjects) * 100 : 0}%`}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* [Amount Invested] end */}

              {/* [Released Projects] start */}
              <div className="col-xxl-3 col-md-6">
                <div className="card stretch stretch-full">
                  <div className="card-body">
                    <div className="d-flex align-items-start justify-content-between mb-4">
                      <div className="d-flex gap-4 align-items-center">
                        <div className="avatar-text avatar-lg bg-gray-200">
                          <i className="feather-check-circle"></i>
                        </div>
                        <div>
                          <div className="fs-4 fw-bold text-dark">
                            {investmentData.isLoading ? '...' : `${investmentData.releasedCount}/${investmentData.totalProjects}`}
                          </div>
                          <h3 className="fs-13 fw-semibold text-truncate-1-line">Released Projects</h3>
                        </div>
                      </div>
                      <a href="javascript:void(0);" className="">
                        <i className="feather-more-vertical"></i>
                      </a>
                    </div>
                    <div className="pt-4">
                      <div className="d-flex align-items-center justify-content-between">
                        <a href="javascript:void(0);" className="fs-12 fw-medium text-muted text-truncate-1-line">Released Investment</a>
                        <div className="w-100 text-end">
                          <span className="fs-12 text-dark">${investmentData.isLoading ? '...' : investmentData.releasedProjects.toLocaleString()}</span>
                          <span className="fs-11 text-muted">({investmentData.amountInvested > 0 ? Math.round((investmentData.releasedProjects / investmentData.amountInvested) * 100) : 0}%)</span>
                        </div>
                      </div>
                      <div className="progress mt-2 ht-3">
                        <div className="progress-bar bg-success" role="progressbar" style={{width: `${investmentData.amountInvested > 0 ? (investmentData.releasedProjects / investmentData.amountInvested) * 100 : 0}%`}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* [Released Projects] end */}

              {/* [Unreleased Projects] start */}
              <div className="col-xxl-3 col-md-6">
                <div className="card stretch stretch-full">
                  <div className="card-body">
                    <div className="d-flex align-items-start justify-content-between mb-4">
                      <div className="d-flex gap-4 align-items-center">
                        <div className="avatar-text avatar-lg bg-gray-200">
                          <i className="feather-clock"></i>
                        </div>
                        <div>
                          <div className="fs-4 fw-bold text-dark">
                            {investmentData.isLoading ? '...' : `${investmentData.unreleasedCount}/${investmentData.totalProjects}`}
                          </div>
                          <h3 className="fs-13 fw-semibold text-truncate-1-line">Unreleased Projects</h3>
                        </div>
                      </div>
                      <a href="javascript:void(0);" className="">
                        <i className="feather-more-vertical"></i>
                      </a>
                    </div>
                    <div className="pt-4">
                      <div className="d-flex align-items-center justify-content-between">
                        <a href="javascript:void(0);" className="fs-12 fw-medium text-muted text-truncate-1-line">Unreleased Investment</a>
                        <div className="w-100 text-end">
                          <span className="fs-12 text-dark">${investmentData.isLoading ? '...' : investmentData.unreleasedAmount.toLocaleString()}</span>
                          <span className="fs-11 text-muted">({investmentData.amountInvested > 0 ? Math.round((investmentData.unreleasedAmount / investmentData.amountInvested) * 100) : 0}%)</span>
                        </div>
                      </div>
                      <div className="progress mt-2 ht-3">
                        <div className="progress-bar bg-warning" role="progressbar" style={{width: `${investmentData.amountInvested > 0 ? (investmentData.unreleasedAmount / investmentData.amountInvested) * 100 : 0}%`}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* [Unreleased Projects] end */}

              {/* [ROI Rate] start */}
              <div className="col-xxl-3 col-md-6">
                <div className="card stretch stretch-full">
                  <div className="card-body">
                    <div className="d-flex align-items-start justify-content-between mb-4">
                      <div className="d-flex gap-4 align-items-center">
                        <div className="avatar-text avatar-lg bg-gray-200">
                          <i className="feather-trending-up"></i>
                        </div>
                        <div>
                          <div className="fs-4 fw-bold text-dark">
                            {investmentData.isLoading ? '...' : `${investmentData.overallROI.toFixed(1)}%`}
                          </div>
                          <h3 className="fs-13 fw-semibold text-truncate-1-line">Overall ROI</h3>
                        </div>
                      </div>
                      <a href="javascript:void(0);" className="">
                        <i className="feather-more-vertical"></i>
                      </a>
                    </div>
                    <div className="pt-4">
                      <div className="d-flex align-items-center justify-content-between">
                        <a href="javascript:void(0);" className="fs-12 fw-medium text-muted text-truncate-1-line">Return on Investment</a>
                        <div className="w-100 text-end">
                          <span className="fs-12 text-dark">{investmentData.releasedCount} Projects</span>
                          <span className="fs-11 text-muted">Released</span>
                        </div>
                      </div>
                      <div className="progress mt-2 ht-3">
                        <div className="progress-bar bg-info" role="progressbar" style={{width: `${Math.min(investmentData.overallROI, 100)}%`}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* [ROI Rate] end */}
            </div>

                 {/* Investment Table Section */}
                 <div className="row mt-4">
                    <div className={collapseDetail ? "col-12" : "col-lg-6"}>
                <div className="card shadow-sm border-0">
                  <div className="card-header bg-white border-bottom">
                    <div className={`d-flex justify-content-between align-items-start w-100`}>
                      <div>
                        <h4 className="card-title mb-1 fw-bold text-dark d-flex align-items-center flex-wrap">
                          <i className="feather-briefcase me-2 text-primary"></i>
                          All Investments
                          {!collapseDetail && (
                            <button 
                              className="btn btn-sm btn-outline-secondary ms-2" 
                              onClick={() => setCollapseDetail(true)}
                            >
                              <i className="feather-maximize-2 me-1 fs-11"></i>
                              Expand
                            </button>
                          )}
                          {collapseDetail && (
                            <button 
                              className="btn btn-sm btn-outline-primary ms-2" 
                              onClick={() => setCollapseDetail(false)}
                            >
                              <i className="feather-minimize-2 me-1 fs-11"></i>
                              Collapse
                            </button>
                          )}
                        </h4>
                        <p className="text-muted mb-0 fs-12">Manage and track your investment portfolio</p>
                      </div>
                      <div className={`d-flex align-items-center ${!collapseDetail ? 'gap-2 flex-wrap' : 'gap-3'}`}>
                        {/* Search Input */}
                        <div className="position-relative" style={{ width: !collapseDetail ? '200px' : '300px' }}>
                          <div className="input-group">
                            <span className="input-group-text bg-white border-end-0" style={{ padding: !collapseDetail ? '4px 8px' : '8px 12px' }}>
                              <i className={`feather-search text-muted ${!collapseDetail ? 'fs-12' : 'fs-14'}`}></i>
                            </span>
                            <input
                              type="text"
                              className="form-control border-start-0 bg-white"
                              placeholder={!collapseDetail ? "Search..." : "Search projects..."}
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              style={{ 
                                borderLeft: 'none',
                                fontSize: !collapseDetail ? '12px' : '14px',
                                padding: !collapseDetail ? '4px 8px' : '8px 12px'
                              }}
                            />
                            {searchTerm && (
                              <button
                                className="btn btn-outline-secondary border-start-0"
                                type="button"
                                onClick={() => setSearchTerm('')}
                                style={{ 
                                  borderLeft: 'none', 
                                  padding: !collapseDetail ? '4px 6px' : '8px',
                                  fontSize: !collapseDetail ? '10px' : '12px'
                                }}
                              >
                                <i className={`feather-x ${!collapseDetail ? 'fs-10' : 'fs-12'}`}></i>
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Filter Dropdown */}
                        <div className="position-relative">
                          <button 
                            className="btn btn-outline-primary btn-sm d-flex align-items-center" 
                            type="button" 
                            onClick={() => setShowFilters(!showFilters)}
                            style={{ 
                              padding: !collapseDetail ? '4px 8px' : '6px 12px',
                              fontSize: !collapseDetail ? '10px' : '13px',
                              fontWeight: '500',
                              gap: !collapseDetail ? '4px' : '8px'
                            }}
                          >
                            <i className={`feather-filter ${!collapseDetail ? 'fs-10' : 'fs-13'}`}></i>
                            {collapseDetail && <span>Filters</span>}
                            <i className={`feather-chevron-down ${!collapseDetail ? 'fs-9' : 'fs-11'} ${showFilters ? 'rotate-180' : ''}`} style={{ transition: 'transform 0.2s ease' }}></i>
                          </button>
                          {showFilters && (
                            <div 
                              className="bg-white border rounded shadow-lg position-absolute"
                              style={{ 
                                minWidth: !collapseDetail ? '240px' : '280px', 
                                top: '100%', 
                                right: '0', 
                                zIndex: 9999,
                                marginTop: '4px',
                                padding: !collapseDetail ? '6px 0' : '8px 0'
                              }}
                            >
                              <div className={`px-3 ${!collapseDetail ? 'py-1' : 'py-2'} border-bottom`}>
                                <h6 className={`mb-0 fw-semibold text-dark ${!collapseDetail ? 'fs-11' : 'fs-12'}`}>Investment Status</h6>
                              </div>
                              
                              <div className={`px-3 ${collapseDetail ? 'py-1' : 'py-2'}`}>
                                <div className="form-check d-flex align-items-center">
                                  <input
                                    className="form-check-input me-3"
                                    type="checkbox"
                                    id="ongoing-card-dropdown"
                                    checked={filters.ongoing}
                                    onChange={(e) => setFilters(prev => ({ ...prev, ongoing: e.target.checked }))}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <label className="form-check-label d-flex align-items-center w-100" htmlFor="ongoing-card-dropdown">
                                    <i className={`feather-clock me-2 text-primary ${collapseDetail ? 'fs-11' : 'fs-13'}`}></i>
                                    <span className={`text-primary fw-medium ${collapseDetail ? 'fs-11' : 'fs-12'}`}>Ongoing</span>
                                  </label>
                                </div>
                              </div>
                              
                              <div className={`px-3 ${collapseDetail ? 'py-1' : 'py-2'}`}>
                                <div className="form-check d-flex align-items-center">
                                  <input
                                    className="form-check-input me-3"
                                    type="checkbox"
                                    id="completed-card-dropdown"
                                    checked={filters.completed}
                                    onChange={(e) => setFilters(prev => ({ ...prev, completed: e.target.checked }))}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <label className="form-check-label d-flex align-items-center w-100" htmlFor="completed-card-dropdown">
                                    <i className={`feather-check-circle me-2 text-success ${collapseDetail ? 'fs-11' : 'fs-13'}`}></i>
                                    <span className={`text-success fw-medium ${collapseDetail ? 'fs-11' : 'fs-12'}`}>Completed</span>
                                  </label>
                                </div>
                              </div>
                              
                              <div className={`px-3 ${collapseDetail ? 'py-1' : 'py-2'}`}>
                                <div className="form-check d-flex align-items-center">
                                  <input
                                    className="form-check-input me-3"
                                    type="checkbox"
                                    id="unreleased-card-dropdown"
                                    checked={filters.unreleased}
                                    onChange={(e) => setFilters(prev => ({ ...prev, unreleased: e.target.checked }))}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <label className="form-check-label d-flex align-items-center w-100" htmlFor="unreleased-card-dropdown">
                                    <i className={`feather-pause-circle me-2 text-danger ${collapseDetail ? 'fs-11' : 'fs-13'}`}></i>
                                    <span className={`text-danger fw-medium ${collapseDetail ? 'fs-11' : 'fs-12'}`}>Unreleased</span>
                                  </label>
                                </div>
                              </div>
                              
                              <div className="border-top my-2"></div>
                              
                              <div className={`px-3 ${collapseDetail ? 'py-1' : 'py-2'} border-bottom`}>
                                <h6 className={`mb-0 fw-semibold text-dark ${collapseDetail ? 'fs-11' : 'fs-12'}`}>Participant</h6>
                              </div>
                              
                              <div className={`px-3 ${collapseDetail ? 'py-1' : 'py-2'}`}>
                                <div className="position-relative">
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm w-100 d-flex align-items-center justify-content-between"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowParticipantDropdown(!showParticipantDropdown);
                                      if (!showParticipantDropdown) {
                                        setParticipantSearchTerm('');
                                      }
                                    }}
                                    disabled={loadingParticipantData}
                                    style={{
                                      fontSize: collapseDetail ? '11px' : '13px',
                                      fontWeight: '500',
                                      padding: collapseDetail ? '4px 8px' : '6px 12px',
                                      border: '1px solid #dee2e6',
                                      backgroundColor: '#fff',
                                      minHeight: collapseDetail ? '32px' : '38px',
                                      transition: 'all 0.2s ease',
                                      opacity: loadingParticipantData ? 0.6 : 1,
                                      cursor: loadingParticipantData ? 'not-allowed' : 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!loadingParticipantData) {
                                        e.currentTarget.style.borderColor = '#adb5bd';
                                        e.currentTarget.style.backgroundColor = '#fff';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!loadingParticipantData) {
                                        e.currentTarget.style.borderColor = '#dee2e6';
                                        e.currentTarget.style.backgroundColor = '#fff';
                                      }
                                    }}
                                    onFocus={(e) => {
                                      e.currentTarget.style.borderColor = '#667eea';
                                      e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.15)';
                                    }}
                                    onBlur={(e) => {
                                      setTimeout(() => {
                                        e.currentTarget.style.borderColor = '#dee2e6';
                                        e.currentTarget.style.boxShadow = 'none';
                                      }, 200);
                                    }}
                                  >
                                    <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                      {loadingParticipantData ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px' }}></span>
                                          <span>Loading...</span>
                                        </>
                                      ) : (
                                        <>
                                          <i className={`feather-user ${collapseDetail ? 'fs-11' : 'fs-13'}`}></i>
                                          <span>
                                            {filters.participantId 
                                              ? participants.find(p => p.id == filters.participantId)?.name || 'Select Participant...'
                                              : 'All Participants'}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                    {!loadingParticipantData && (
                                      <i className={`feather-chevron-down ${collapseDetail ? 'fs-9' : 'fs-11'} ${showParticipantDropdown ? 'rotate-180' : ''}`} style={{ transition: 'transform 0.2s ease' }}></i>
                                    )}
                                  </button>
                                  {showParticipantDropdown && !loadingParticipantData && (
                                    <div 
                                      className="bg-white border rounded shadow-lg position-absolute"
                                      style={{ 
                                        width: '100%', 
                                        top: '100%', 
                                        left: '0', 
                                        zIndex: 9999,
                                        marginTop: '4px',
                                        padding: '8px 0',
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        borderRadius: '8px'
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                      }}
                                    >
                                      {/* Search Input */}
                                      <div className="px-2 pb-1 border-bottom">
                                        <div className="position-relative">
                                          <i className="feather-search position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: '#6c757d' }}></i>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Search participant..."
                                            value={participantSearchTerm}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              setParticipantSearchTerm(value);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => e.stopPropagation()}
                                            style={{
                                              paddingLeft: '24px',
                                              paddingTop: '4px',
                                              paddingBottom: '4px',
                                              fontSize: '11px',
                                              border: '1px solid #dee2e6',
                                              borderRadius: '4px',
                                              height: '28px'
                                            }}
                                          />
                                        </div>
                                      </div>
                                      
                                      {/* All Participants Option */}
                                      {(!participantSearchTerm || 'all participants'.includes(participantSearchTerm.toLowerCase().trim())) && (
                                        <div 
                                          className={`px-2 py-1 ${!filters.participantId ? 'bg-light' : ''}`}
                                          style={{ cursor: 'pointer' }}
                                          onClick={() => {
                                            handleParticipantChange(null);
                                            setParticipantSearchTerm('');
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!filters.participantId) {
                                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                                            } else {
                                              e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = !filters.participantId ? '#f8f9fa' : 'transparent';
                                          }}
                                        >
                                          <div className="d-flex align-items-center">
                                            <i className="feather-users me-2" style={{ fontSize: '11px' }}></i>
                                            <span style={{ fontSize: '11px' }}>All Participants</span>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Filtered Participants List */}
                                      {participants
                                        .filter(participant => {
                                          if (!participantSearchTerm || participantSearchTerm.trim() === '') {
                                            return true;
                                          }
                                          const searchTerm = participantSearchTerm.toLowerCase().trim();
                                          const participantName = (participant.name || '').toLowerCase();
                                          return participantName.includes(searchTerm);
                                        })
                                        .map((participant) => (
                                          <div
                                            key={participant.id}
                                            className={`px-2 py-1 ${filters.participantId == participant.id ? 'bg-light' : ''}`}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                              handleParticipantChange(participant.id);
                                              setParticipantSearchTerm('');
                                            }}
                                            onMouseEnter={(e) => {
                                              if (filters.participantId != participant.id) {
                                                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
                                              } else {
                                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                              }
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.backgroundColor = filters.participantId == participant.id ? '#f8f9fa' : 'transparent';
                                            }}
                                          >
                                            <div className="d-flex align-items-center">
                                              <i className="feather-user me-2" style={{ fontSize: '11px' }}></i>
                                              <span style={{ fontSize: '11px' }}>{participant.name}</span>
                                            </div>
                                          </div>
                                        ))}
                                      
                                      {/* No Results Message */}
                                      {participantSearchTerm && participantSearchTerm.trim() !== '' && participants.filter(p => {
                                        const searchTerm = participantSearchTerm.toLowerCase().trim();
                                        const participantName = (p.name || '').toLowerCase();
                                        return participantName.includes(searchTerm);
                                      }).length === 0 && (
                                        <div className="px-2 py-2 text-center text-muted" style={{ fontSize: '11px' }}>
                                          No participants found
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="border-top my-2"></div>
                              
                              <div className={`px-3 ${collapseDetail ? 'py-1' : 'py-2'}`}>
                                <button 
                                  className="btn btn-outline-secondary btn-sm w-100"
                                  onClick={async () => {
                                    setFilters({
                                      ongoing: true,
                                      completed: false,
                                      unreleased: false,
                                      participantId: null
                                    });
                                    setSearchTerm('');
                                    await handleParticipantChange(null);
                                    setShowFilters(false);
                                  }}
                                  style={{ fontSize: collapseDetail ? '10px' : '12px' }}
                                >
                                  <i className={`feather-refresh-cw me-2 ${collapseDetail ? 'fs-10' : 'fs-11'}`}></i>
                                  Reset Filters
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    {/* Filter Pills */}
                    <div className={`px-4 pt-3 pb-2`}>
                      <div className={`d-flex align-items-center justify-content-between flex-wrap ${!collapseDetail ? 'gap-2' : 'gap-3'}`}>
                        <div className={`d-flex align-items-center flex-wrap ${!collapseDetail ? 'gap-2' : 'gap-3'}`}>
                          <div className="d-flex align-items-center gap-2">
                            <span className={`text-muted fw-semibold ${!collapseDetail ? 'fs-11' : 'fs-12'}`}>Status:</span>
                            <div className="d-flex gap-1">
                              <span className={`badge ${filters.ongoing ? 'bg-primary text-white' : 'bg-light text-muted border'} ${!collapseDetail ? 'px-2 py-1' : 'px-3 py-2'} ${!collapseDetail ? 'fs-10' : 'fs-11'} fw-medium`}>
                                <i className={`feather-clock me-1 ${filters.ongoing ? 'text-white' : 'text-muted'} ${!collapseDetail ? 'fs-9' : 'fs-10'}`}></i>
                                {!collapseDetail ? 'Ongoing' : 'Ongoing'}
                              </span>
                              <span className={`badge ${filters.completed ? 'bg-success text-white' : 'bg-light text-muted border'} ${!collapseDetail ? 'px-2 py-1' : 'px-3 py-2'} ${!collapseDetail ? 'fs-10' : 'fs-11'} fw-medium`}>
                                <i className={`feather-check-circle me-1 ${filters.completed ? 'text-white' : 'text-muted'} ${!collapseDetail ? 'fs-9' : 'fs-10'}`}></i>
                                {!collapseDetail ? 'Done' : 'Completed'}
                              </span>
                              <span className={`badge ${filters.unreleased ? 'bg-danger text-white' : 'bg-light text-muted border'} ${!collapseDetail ? 'px-2 py-1' : 'px-3 py-2'} ${!collapseDetail ? 'fs-10' : 'fs-11'} fw-medium`}>
                                <i className={`feather-pause-circle me-1 ${filters.unreleased ? 'text-white' : 'text-muted'} ${!collapseDetail ? 'fs-9' : 'fs-10'}`}></i>
                                {!collapseDetail ? 'Pending' : 'Unreleased'}
                              </span>
                            </div>
                            {!collapseDetail && (
                              <span className="ms-2">
                                <span className="badge bg-primary text-white ${!collapseDetail ? 'px-2 py-1' : 'px-3 py-2'} ${!collapseDetail ? 'fs-10' : 'fs-11'}">
                                  {filteredProjects.length} results
                                </span>
                              </span>
                            )}
                          </div>
                          
                          {filters.participantId && (
                            <div className="d-flex align-items-center gap-2">
                              <span className={`text-muted fw-semibold fs-12`}>Participant:</span>
                              <span className={`badge bg-info text-white ${!collapseDetail ? 'px-2 py-1' : 'px-3 py-2'} ${!collapseDetail ? 'fs-10' : 'fs-11'} fw-medium d-flex align-items-center gap-2`}>
                                <i className={`feather-user me-1 fs-10`}></i>
                                <span>{(participants.find(p => p.id == filters.participantId)?.name || 'Selected Participant')}</span>
                                <button 
                                  className="btn btn-sm p-0 border-0 bg-transparent text-white"
                                  onClick={async () => await handleParticipantChange(null)}
                                  style={{ 
                                    fontSize: '10px', 
                                    padding: '0',
                                    lineHeight: '1',
                                    width: '16px',
                                    height: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  title="Remove participant filter"
                                >
                                  <i className={`feather-x fs-10`}></i>
                                </button>
                              </span>
                            </div>
                          )}
                          
                          {searchTerm && (
                            <div className="d-flex align-items-center gap-2">
                              <span className={`text-muted fw-semibold fs-12`}>Search:</span>
                              <span className={`badge bg-warning text-dark px-3 py-2 fs-11 fw-medium d-flex align-items-center gap-2`}>
                                <i className={`feather-search me-1 fs-10`}></i>
                                <span>"{searchTerm}"</span>
                                <button 
                                  className="btn btn-sm p-0 border-0 bg-transparent text-dark"
                                  onClick={() => setSearchTerm('')}
                                  style={{ 
                                    fontSize:  '10px', 
                                    padding: '0',
                                    lineHeight: '1',
                                    width: '16px',
                                    height: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  title="Clear search"
                                >
                                  <i className={`feather-x 'fs-10'}`}></i>
                                </button>
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <div className={`d-flex align-items-center ${collapseDetail ? 'gap-2' : 'gap-3'}`}>
                          {filteredProjects.length > 0 && collapseDetail &&(
                            <div className="d-flex align-items-center gap-2">
                              <span className={`text-muted fw-medium fs-12`}>Results:</span>
                              <span className={`badge bg-dark text-white px-3 py-2 fs-11 fw-medium`}>
                                {filteredProjects.length} of {investmentDetails.length}
                              </span>
                            </div>
                          )}
                          
                          {(filters.participantId || searchTerm || !filters.ongoing || filters.completed || filters.unreleased) && (
                            <button 
                              className="btn btn-outline-secondary btn-sm"
                              onClick={async () => {
                                setFilters({
                                  ongoing: true,
                                  completed: false,
                                  unreleased: false,
                                  participantId: null
                                });
                                setSearchTerm('');
                                await handleParticipantChange(null);
                              }}
                              style={{ fontSize: !collapseDetail ? '10px' : '12px', padding: !collapseDetail ? '2px 6px' : '4px 8px' }}
                            >
                              <i className={`feather-x ${collapseDetail ? 'fs-9' : 'fs-10'}`}></i>
                              {!collapseDetail ? 'Clear' : 'Clear All'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Investment Table */}
                    <div className="table-responsive">
                      <table className="table table-hover mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                          <tr>
                            <th className={`border-0 ${collapseDetail ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark ${collapseDetail ? 'fs-14' : 'fs-12'}`} style={{ minWidth: collapseDetail ? '120px' : '140px', width: collapseDetail ? '18%' : '20%' }}>
                              <i className={`feather-briefcase me-1 text-muted ${collapseDetail ? 'fs-13' : 'fs-11'}`}></i>
                              {collapseDetail ? 'Project' : 'Project Name'}
                            </th>
                            <th className={`border-0 ${collapseDetail ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark ${collapseDetail ? 'fs-14' : 'fs-12'} text-end`} style={{ minWidth: collapseDetail ? '100px' : '120px', width: collapseDetail ? '16%' : '18%' }}>
                              <i className={`feather-dollar-sign me-1 text-muted ${collapseDetail ? 'fs-13' : 'fs-11'}`}></i>
                              {collapseDetail ? 'Amount' : 'Allocation'}
                            </th>
                            <th className={`border-0 ${collapseDetail ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark ${collapseDetail ? 'fs-14' : 'fs-12'} text-end`} style={{ minWidth: collapseDetail ? '80px' : '100px', width: '14%' }}>
                              <i className={`feather-tag me-1 text-muted ${collapseDetail ? 'fs-13' : 'fs-11'}`}></i>
                              {collapseDetail ? 'Price' : 'Token Price'}
                            </th>
                            <th className={`border-0 ${collapseDetail ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark ${collapseDetail ? 'fs-14' : 'fs-12'} text-end`} style={{ minWidth: collapseDetail ? '80px' : '100px', width: '14%' }}>
                              <i className={`feather-trending-up me-1 text-muted ${collapseDetail ? 'fs-13' : 'fs-11'}`}></i>
                              {collapseDetail ? 'Current' : 'Current Price'}
                            </th>
                            <th className={`border-0 ${collapseDetail ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark ${collapseDetail ? 'fs-14' : 'fs-12'} text-end`} style={{ minWidth: collapseDetail ? '60px' : '80px', width: '12%' }}>
                              <i className={`feather-percent me-1 text-muted ${collapseDetail ? 'fs-13' : 'fs-11'}`}></i>
                              {collapseDetail ? 'Release' : 'Release'}
                            </th>
                            <th className={`border-0 ${collapseDetail ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark ${collapseDetail ? 'fs-14' : 'fs-12'} text-end`} style={{ minWidth: collapseDetail ? '70px' : '90px', width: '13%' }}>
                              <i className={`feather-percent me-1 text-muted ${collapseDetail ? 'fs-13' : 'fs-11'}`}></i>
                              {collapseDetail ? 'PC (NTC)' : 'PC (NTC)'}
                            </th>
                            <th className={`border-0 ${collapseDetail ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark ${collapseDetail ? 'fs-14' : 'fs-12'} text-end`} style={{ minWidth: collapseDetail ? '70px' : '90px', width: '13%' }}>
                              <i className={`feather-percent me-1 text-muted ${collapseDetail ? 'fs-13' : 'fs-11'}`}></i>
                              {collapseDetail ? 'PC (Actual)' : 'PC (Actual)'}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredProjects.length > 0 ? (
                            filteredProjects.map((inv, index) => (
                              <tr key={index} className="border-bottom">
                                <td className={`${collapseDetail ? 'px-2 py-2' : 'px-3 py-2'}`}>
                                  <div className="d-flex align-items-center">
                                    <div className={`avatar-text avatar-sm me-2 ${
                                      inv.status === 'ongoing' ? 'bg-primary' : 
                                      inv.status === 'completed' ? 'bg-success' : 'bg-danger'
                                    }`} style={{ width: '32px', height: '32px' }}>
                                      <i className={`feather-${
                                        inv.status === 'ongoing' ? 'clock' : 
                                        inv.status === 'completed' ? 'check-circle' : 'pause-circle'
                                      } text-white fs-12`}></i>
                                    </div>
                                         <div style={{ minWidth: 0, maxWidth: '100%' }}>
                                           <h6 className={`mb-0 fw-semibold ${collapseDetail ? 'fs-13' : 'fs-12'} ${
                                             inv.status === 'ongoing' ? 'text-primary' : 
                                             inv.status === 'completed' ? 'text-success' : 'text-danger'
                                           }`} style={{ 
                                             overflow: 'hidden',
                                             textOverflow: 'ellipsis',
                                             whiteSpace: 'nowrap',
                                             maxWidth: '100%'
                                           }}>
                                             <a 
                                               href="javascript:void(0)" 
                                               onClick={() => showReleaseDetails(inv, false)}
                                               className="text-decoration-none"
                                               style={{ 
                                                 color: inv.status === 'ongoing' ? '#0d6efd' : 
                                                        inv.status === 'completed' ? '#198754' : '#dc3545',
                                                 display: 'inline-block',
                                                 maxWidth: '100%',
                                                 overflow: 'hidden',
                                                 textOverflow: 'ellipsis',
                                                 whiteSpace: 'nowrap'
                                               }}
                                               title={inv.name}
                                             >
                                               {inv.name}
                                             </a>
                                           </h6>
                                           {collapseDetail && <small className="text-muted fs-11">{inv.chain}</small>}
                                         </div>
                                  </div>
                                </td>
                                <td className={`${collapseDetail ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                  <span className={`fw-semibold text-dark ${collapseDetail ? 'fs-13' : 'fs-12'}`}>
                                    ${Math.round(inv.allocation_amount).toLocaleString()}
                                  </span>
                                </td>
                                <td className={`${collapseDetail ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                  <span className={`fw-medium text-dark ${collapseDetail ? 'fs-13' : 'fs-12'}`}>
                                    {inv.token_price ? `$${collapseDetail ? parseFloat(inv.token_price).toFixed(2) : inv.token_price}` : '-'}
                                  </span>
                                </td>
                                <td className={`${collapseDetail ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                  {coinGeckoData.length > 0 && coinGeckoData.find(coin => coin.id === inv.coingecko_symbol)?.current_price ? (
                                    <span className={`fw-medium ${collapseDetail ? 'fs-13' : 'fs-12'} ${
                                      parseFloat(coinGeckoData.find(coin => coin.id === inv.coingecko_symbol)?.current_price) > parseFloat(inv.token_price) ? 'text-success' : 'text-danger'
                                    }`}>
                                      ${parseFloat(coinGeckoData.find(coin => coin.id === inv.coingecko_symbol)?.current_price).toFixed(5)}
                                    </span>
                                  ) : (
                                    <span className={`fw-medium text-muted ${collapseDetail ? 'fs-13' : 'fs-12'}`}>
                                      -
                                    </span>
                                  )}
                                </td>
                                <td className={`${collapseDetail ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                  <span className={`fw-semibold ${collapseDetail ? 'fs-13' : 'fs-12'} ${
                                    parseFloat(inv.release_per || 0) > 0 ? 'text-info' : 'text-muted'
                                  }`}>
                                    {inv.release_per ? `${parseFloat(inv.release_per).toFixed(collapseDetail ? 1 : 2)}%` : '-'}
                                  </span>
                                </td>
                                <td className={`${collapseDetail ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                  <span className={`fw-semibold ${collapseDetail ? 'fs-13' : 'fs-12'} ${
                                    parseFloat(inv.ntc_roi || 0) >= 100 ? 'text-success' : 
                                    parseFloat(inv.ntc_roi || 0) > 0 ? 'text-warning' : 'text-danger'
                                  }`}>
                                    {inv.ntc_roi ? `${parseFloat(inv.ntc_roi).toFixed(collapseDetail ? 1 : 2)}%` : '-'}
                                  </span>
                                </td>
                                <td className={`${collapseDetail ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                  <span className={`fw-semibold ${collapseDetail ? 'fs-13' : 'fs-12'} ${
                                    parseFloat(inv.actual_roi || 0) >= 100 ? 'text-success' : 
                                    parseFloat(inv.actual_roi || 0) > 0 ? 'text-warning' : 'text-danger'
                                  }`}>
                                    {inv.actual_roi ? `${parseFloat(inv.actual_roi).toFixed(collapseDetail ? 1 : 2)}%` : '-'}
                                  </span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center py-5">
                                <div className="text-muted">
                                  <i className="feather-search fs-1 mb-3 d-block"></i>
                                  <h5 className="text-muted">No projects found</h5>
                                  <p className="text-muted fs-12">Try adjusting your filters or search terms</p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Table Footer */}
                    <div className={`card-footer bg-light border-top ${collapseDetail ? 'px-2 py-2' : 'px-4 py-3'}`}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <small className={`text-muted ${collapseDetail ? 'fs-10' : 'fs-11'}`}>
                            <strong>% PC</strong> - % Principal Covered. 
                            {!collapseDetail && <><strong>**</strong> Current prices are being pulled from COINGECKO tracker.</>}
                          </small>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <small className={`text-muted ${collapseDetail ? 'fs-10' : 'fs-11'}`}>Total Projects:</small>
                          <span className={`badge bg-primary ${collapseDetail ? 'fs-10' : 'fs-11'}`}>{investmentDetails.length}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Info and Release Details */}
              {!collapseDetail && (
                <div className="col-lg-6">
                  <div className="row g-3">
                    {/* Project Tokenomics Info Card */}
                    <div className="col-12">
                      <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-bottom">
                          <div className="d-flex justify-content-between align-items-start w-100">
                            <div>
                              <h4 className="card-title mb-1 fw-bold text-dark">
                                <i className="feather-info me-2 text-primary"></i>
                                Project Information
                                {selectedProject && (
                                  <span className="text-muted ms-2">- {selectedProject.name}</span>
                                )}
                              </h4>
                              <p className="text-muted mb-0 fs-12">Token details and performance metrics</p>
                            </div>
                          </div>
                        </div>

                        <div className="card-body p-3">
                          {selectedProject ? (
                            <>
                              {/* Project Tokenomics Info */}
                              <div className="d-flex flex-wrap gap-3 align-items-center">
                              {/* Symbol */}
                              <div className="d-flex align-items-center gap-2 px-3 py-2 border rounded-pill" style={{borderStyle: 'dashed', borderColor: '#e9ecef'}}>
                                <div className="rounded-circle" style={{width: '8px', height: '8px', backgroundColor: '#3b82f6'}}></div>
                                <span className="fw-semibold text-dark fs-13">{selectedProject.token_symbol}</span>
                                <small className="text-muted fs-12">(Symbol)</small>
                              </div>
                              
                              {/* Token Price */}
                              <div className="d-flex align-items-center gap-2 px-3 py-2 border rounded-pill" style={{borderStyle: 'dashed', borderColor: '#e9ecef'}}>
                                <div className="rounded-circle" style={{width: '8px', height: '8px', backgroundColor: '#10b981'}}></div>
                                <span className="fw-semibold text-dark fs-13">${selectedProject.token_price}</span>
                                <small className="text-muted fs-12">(Token Price)</small>
                              </div>
                              
                              {/* Token Chain */}
                              <div className="d-flex align-items-center gap-2 px-3 py-2 border rounded-pill" style={{borderStyle: 'dashed', borderColor: '#e9ecef'}}>
                                <div className="rounded-circle" style={{width: '8px', height: '8px', backgroundColor: '#f59e0b'}}></div>
                                <span className="fw-semibold text-dark fs-13">{selectedProject.chain}</span>
                                <small className="text-muted fs-12">Chain</small>
                              </div>
                              
                              {/* Tokens Released */}
                              <div className="d-flex align-items-center gap-2 px-3 py-2 border rounded-pill" style={{borderStyle: 'dashed', borderColor: '#e9ecef'}}>
                                <div className="rounded-circle" style={{width: '8px', height: '8px', backgroundColor: '#8b5cf6'}}></div>
                                <span className="fw-semibold text-dark fs-13">
                                  {(() => {
                                    const totalReleasedTokens = releaseDetails.reduce((sum, release) => {
                                      const releasedTokens = Math.round(selectedProject.allocation_amount) * (1 - parseFloat(selectedProject.management_fee) / 100) * parseFloat(release.release_per) / (parseFloat(selectedProject.token_price) * 100);
                                      return sum + releasedTokens;
                                    }, 0);
                                    const totalTokens = Math.round(selectedProject.allocation_amount) * (1 - parseFloat(selectedProject.management_fee) / 100) / parseFloat(selectedProject.token_price);
                                    return `${totalReleasedTokens.toLocaleString(undefined, {maximumFractionDigits:2})} / ${totalTokens.toLocaleString(undefined, {maximumFractionDigits:2})}`;
                                  })()}
                                </span>
                                <small className="text-muted fs-12">Released</small>
                              </div>
                              
                              {/* Total Release % */}
                              <div className="d-flex align-items-center gap-2 px-3 py-2 border rounded-pill" style={{borderStyle: 'dashed', borderColor: '#e9ecef'}}>
                                <div className="rounded-circle" style={{width: '8px', height: '8px', backgroundColor: '#06b6d4'}}></div>
                                <span className="fw-semibold text-dark fs-13">
                                  {releaseDetails.reduce((sum, release) => sum + parseFloat(release.release_per), 0).toFixed(2)}%
                                </span>
                                <small className="text-muted fs-12">Release %</small>
                              </div>
                              
                              {/* % ROI (NTC) */}
                              <div className="d-flex align-items-center gap-2 px-3 py-2 border rounded-pill" style={{borderStyle: 'dashed', borderColor: '#e9ecef'}}>
                                <div className="rounded-circle" style={{width: '8px', height: '8px', backgroundColor: '#ef4444'}}></div>
                                <span className={`fw-semibold fs-13 ${(() => {
                                  const totalReleaseValue = releaseDetails.reduce((sum, release) => {
                                    const releasedTokens = Math.round(selectedProject.allocation_amount) * (1 - parseFloat(selectedProject.management_fee) / 100) * parseFloat(release.release_per) / (parseFloat(selectedProject.token_price) * 100);
                                    return sum + (parseFloat(release.price_at_release) * releasedTokens);
                                  }, 0);
                                  const roi = (totalReleaseValue / selectedProject.allocation_amount) * 100;
                                  return roi > 100 ? 'text-success' : 'text-danger';
                                })()}`}>
                                  {(() => {
                                    const totalReleaseValue = releaseDetails.reduce((sum, release) => {
                                      const releasedTokens = Math.round(selectedProject.allocation_amount) * (1 - parseFloat(selectedProject.management_fee) / 100) * parseFloat(release.release_per) / (parseFloat(selectedProject.token_price) * 100);
                                      return sum + (parseFloat(release.price_at_release) * releasedTokens);
                                    }, 0);
                                    return ((totalReleaseValue / selectedProject.allocation_amount) * 100).toFixed(2);
                                  })()}%
                                </span>
                                <small className="text-muted fs-12">ROI (NTC)</small>
                              </div>
                              
                              {/* % ROI (Actual) */}
                              <div className="d-flex align-items-center gap-2 px-3 py-2 border rounded-pill" style={{borderStyle: 'dashed', borderColor: '#e9ecef'}}>
                                <div className="rounded-circle" style={{width: '8px', height: '8px', backgroundColor: '#84cc16'}}></div>
                                <span className={`fw-semibold fs-13 ${(() => {
                                  const totalActualValue = releaseDetails.reduce((sum, release) => {
                                    const releasedTokens = Math.round(selectedProject.allocation_amount) * (1 - parseFloat(selectedProject.management_fee) / 100) * parseFloat(release.release_per) / (parseFloat(selectedProject.token_price) * 100);
                                    const actualSellingPrice = releaseParticipantSellingData.find(x => x.project_release_id === release.id)?.selling_price;
                                    return sum + (actualSellingPrice ? parseFloat(actualSellingPrice) * releasedTokens : 0);
                                  }, 0);
                                  const roi = (totalActualValue / selectedProject.allocation_amount) * 100;
                                  return roi > 100 ? 'text-success' : 'text-danger';
                                })()}`}>
                                  {(() => {
                                    const totalActualValue = releaseDetails.reduce((sum, release) => {
                                      const releasedTokens = Math.round(selectedProject.allocation_amount) * (1 - parseFloat(selectedProject.management_fee) / 100) * parseFloat(release.release_per) / (parseFloat(selectedProject.token_price) * 100);
                                      const actualSellingPrice = releaseParticipantSellingData.find(x => x.project_release_id === release.id)?.selling_price;
                                      return sum + (actualSellingPrice ? parseFloat(actualSellingPrice) * releasedTokens : 0);
                                    }, 0);
                                    return ((totalActualValue / selectedProject.allocation_amount) * 100).toFixed(2);
                                  })()}%
                                </span>
                                <small className="text-muted fs-12">ROI (Actual)</small>
                              </div>
                            </div>
                            
                            {/* Token Contract Address */}
                            <div className="mt-3 d-flex align-items-center gap-2 px-3 py-2 border rounded-pill" style={{borderStyle: 'dashed', borderColor: '#e9ecef', maxWidth: 'fit-content'}}>
                              <div className="rounded-circle" style={{width: '8px', height: '8px', backgroundColor: '#6366f1'}}></div>
                              <span className="fw-semibold text-dark fs-13" style={{fontFamily: 'monospace'}}>
                                {selectedProject.token_contract_addr ? 
                                  `${selectedProject.token_contract_addr}` : 
                                  'N/A'
                                }
                              </span>
                              <button 
                                className="btn btn-sm btn-outline-secondary p-1" 
                                title="Copy to clipboard" 
                                onClick={() => {
                                  navigator.clipboard.writeText(selectedProject.token_contract_addr);
                                }}
                                style={{width: '24px', height: '24px'}}
                              >
                                <i className="feather-copy" style={{fontSize: '12px'}}></i>
                              </button>
                              <small className="text-muted fs-12">Contract Address</small>
                            </div>
                            
                            {/* Vesting Info */}
                            {(selectedProject.status === 'ongoing' || selectedProject.status === 'completed') && vestingSchedule.length > 0 && (
                              <div className="mt-3">
                                <div className="d-flex flex-wrap gap-2 align-items-center">
                                  {(() => {
                                    const projectVesting = vestingSchedule.find(x => x.proj_id === selectedProject.proj_id);
                                    if (!projectVesting) return null;

                                    const tgeDate = new Date(projectVesting.tge_date).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: '2-digit' 
                                    });

                                    const vestingItems = [
                                      {
                                        label: 'TGE Date',
                                        value: tgeDate,
                                        color: '#f97316'
                                      },
                                      {
                                        label: 'TGE Release',
                                        value: `${projectVesting.tge_release}%`,
                                        color: '#3b82f6'
                                      },
                                      {
                                        label: 'Cliff Period',
                                        value: `${projectVesting.cliff_period} months`,
                                        color: '#ef4444'
                                      },
                                      {
                                        label: 'Release Rate',
                                        value: `${projectVesting.release_per_time}%`,
                                        color: '#10b981'
                                      },
                                      {
                                        label: 'Periods',
                                        value: `${projectVesting.no_of_repitition}`,
                                        color: '#8b5cf6'
                                      },
                                      {
                                        label: 'Type',
                                        value: projectVesting.vesting_type,
                                        color: '#06b6d4'
                                      }
                                    ];

                                    return vestingItems.map((item, index) => (
                                      <div key={index} className="d-flex align-items-center gap-2 px-3 py-2 border rounded-pill" style={{borderStyle: 'dashed', borderColor: '#e9ecef'}}>
                                        <div className="rounded-circle" style={{width: '8px', height: '8px', backgroundColor: item.color}}></div>
                                        <span className="fw-semibold text-dark fs-13">{item.value}</span>
                                        <small className="text-muted fs-12">({item.label})</small>
                                      </div>
                                    ));
                                  })()}
                                </div>
                              </div>
                            )}
                            </>
                          ) : (
                            <div className="text-center py-4">
                              <div className="text-muted">
                                <i className="feather-info fs-1 mb-3 d-block"></i>
                                <h5 className="text-muted">No project selected</h5>
                                <p className="text-muted fs-12">Select a project from the left table to view details</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Release Details Table Card */}
                    <div className="col-12">
                      <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-bottom">
                          <div className="d-flex justify-content-between align-items-start w-100">
                            <div>
                              <h4 className="card-title mb-1 fw-bold text-dark">
                                <i className="feather-calendar me-2 text-success"></i>
                                Release Details
                                {selectedProject && (
                                  <span className="text-muted ms-2">- {selectedProject.name}</span>
                                )}
                              </h4>
                              <p className="text-muted mb-0 fs-12">Track token releases and selling prices</p>
                            </div>
                            <div>
                              {selectedProject && (selectedProject.status === 'ongoing' || selectedProject.status === 'completed') && (
                                <button 
                                  className="btn btn-warning btn-sm"
                                  onClick={() => setInsertRelease(true)}
                                >
                                  <i className="feather-plus me-1 fs-11"></i>
                                  Add Release
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="card-body px-3 py-0">
                          {selectedProject ? (
                            <>
                              {/* Add Release Form - Show when insertRelease is true */}
                              {insertRelease ? (
                                <div className="p-4">
                                  <div className="d-flex align-items-center mb-4">
                                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                      <i className="feather-plus fs-5"></i>
                                    </div>
                                    <div className="ms-3">
                                      <h5 className="mb-0 fw-bold text-dark">Add New Release</h5>
                                      <small className="text-muted">Enter release details for {selectedProject.name}</small>
                                    </div>
                                  </div>
                                  
                                  <div className="row g-3">
                                    <div className="col-md-4">
                                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                                        <i className="feather-calendar me-2 text-primary fs-12"></i>
                                        Release Date
                                      </label>
                                      <input 
                                        type="date" 
                                        className="form-control"
                                        value={releaseForm.release_date}
                                        onChange={(e) => setReleaseForm(prev => ({ ...prev, release_date: e.target.value }))}
                                        style={{ borderColor: '#dee2e6' }}
                                      />
                                    </div>
                                    <div className="col-md-4">
                                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                                        <i className="feather-percent me-2 text-primary fs-12"></i>
                                        Release Percentage
                                      </label>
                                      <div className="input-group">
                                        <input 
                                          type="number" 
                                          className="form-control"
                                          placeholder="Enter percentage"
                                          value={releaseForm.release_per}
                                          onChange={(e) => setReleaseForm(prev => ({ ...prev, release_per: e.target.value }))}
                                          style={{ borderColor: '#dee2e6' }}
                                        />
                                        <span className="input-group-text fs-12" style={{ backgroundColor: '#f8f9fa' }}>%</span>
                                      </div>
                                    </div>
                                    <div className="col-md-4">
                                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                                        <i className="feather-dollar-sign me-2 text-primary fs-12"></i>
                                        Price at Release
                                      </label>
                                      <div className="input-group">
                                        <span className="input-group-text fs-12" style={{ backgroundColor: '#f8f9fa' }}>$</span>
                                        <input 
                                          type="number" 
                                          step="0.00001"
                                          className="form-control"
                                          placeholder="Enter price"
                                          value={releaseForm.price_at_release}
                                          onChange={(e) => setReleaseForm(prev => ({ ...prev, price_at_release: e.target.value }))}
                                          style={{ borderColor: '#dee2e6' }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
                                    <button 
                                      className="btn btn-outline-secondary btn-sm px-3"
                                      onClick={() => {
                                        setInsertRelease(false);
                                        setReleaseForm({ release_date: '', release_per: '', price_at_release: '' });
                                      }}
                                      disabled={isSavingRelease}
                                    >
                                      <i className="feather-x me-1 fs-11"></i>
                                      Cancel
                                    </button>
                                    <button 
                                      className="btn btn-primary btn-sm px-3"
                                      onClick={saveRelease}
                                      disabled={isSavingRelease}
                                    >
                                      {isSavingRelease ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                          Saving...
                                        </>
                                      ) : (
                                        <>
                                          <i className="feather-save me-1 fs-11"></i>
                                          Save Release
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>

                          {/* Release Details Table */}
                          <div className="table-responsive">
                            <table className="table table-hover mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                              <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                  <th className="border-0 px-3 py-3 fw-semibold text-dark fs-13" style={{ width: '12%' }}>Date</th>
                                  <th className="border-0 px-3 py-3 fw-semibold text-dark fs-13 text-end" style={{ width: '8%' }}>%</th>
                                  <th className="border-0 px-3 py-3 fw-semibold text-dark fs-13 text-end" style={{ width: '12%' }}>Tokens</th>
                                  <th className="border-0 px-3 py-3 fw-semibold text-dark fs-13 text-end" style={{ width: '12%' }}>Price</th>
                                  <th className="border-0 px-3 py-3 fw-semibold text-dark fs-13 text-end" style={{ width: '12%' }}>$ (NTC)</th>
                                  <th className="border-0 px-3 py-3 fw-semibold text-dark fs-13 text-end" style={{ width: '16%' }}>Selling Price</th>
                                  <th className="border-0 px-3 py-3 fw-semibold text-dark fs-13 text-end" style={{ width: '12%' }}>$ (Actual)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {releaseDetails.length > 0 && selectedProject ? (
                                  releaseDetails.map((release, index) => {
                                    // Calculate number of tokens released
                                    const releasedTokens = Math.round(selectedProject.allocation_amount) * (1 - parseFloat(selectedProject.management_fee) / 100) * parseFloat(release.release_per) / (parseFloat(selectedProject.token_price) * 100);
                                    const soldNTC = parseFloat(release.price_at_release) * releasedTokens;
                                    const actualSellingPrice = releaseParticipantSellingData.find(x => x.project_release_id === release.id)?.selling_price || '';
                                    const soldActual = actualSellingPrice ? parseFloat(actualSellingPrice) * releasedTokens : 0;
                                    
                                      return (
                                        <tr key={index} className="border-bottom">
                                          <td className="px-3 py-2">
                                            <span className="fw-medium text-dark fs-12">
                                              {new Date(release.release_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-end">
                                            <span className="fw-semibold text-info fs-12">
                                              {parseFloat(release.release_per).toFixed(2)}%
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-end">
                                            <span className="fw-medium text-dark fs-12">
                                              {releasedTokens.toFixed(2)}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-end">
                                            <span className={`fw-semibold fs-12 ${
                                              parseFloat(release.price_at_release) > parseFloat(selectedProject.token_price) ? 'text-success' : 'text-danger'
                                            }`}>
                                              ${parseFloat(release.price_at_release).toFixed(4)}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-end">
                                            <span className="fw-medium text-dark fs-12">
                                              ${soldNTC.toFixed(2)}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-end">
                                            <div className="d-flex align-items-center justify-content-end gap-1">
                                              {editingSellingPrice[release.id] ? (
                                                <div className="d-flex align-items-center gap-1">
                                                  <input 
                                                    type="number" 
                                                    step="0.00001"
                                                    className="form-control form-control-sm"
                                                    style={{ width: '95px' }}
                                                    value={tempSellingPrice[release.id] || ''}
                                                    onChange={(e) => setTempSellingPrice(prev => ({ ...prev, [release.id]: e.target.value }))}
                                                    autoFocus
                                                    onKeyDown={(e) => {
                                                      if (e.key === 'Enter') {
                                                        if (tempSellingPrice[release.id]) {
                                                          saveSellingPrice(release.id, tempSellingPrice[release.id]);
                                                        }
                                                        setEditingSellingPrice(prev => ({ ...prev, [release.id]: false }));
                                                      } else if (e.key === 'Escape') {
                                                        setEditingSellingPrice(prev => ({ ...prev, [release.id]: false }));
                                                      }
                                                    }}
                                                    onBlur={() => {
                                                      if (tempSellingPrice[release.id]) {
                                                        saveSellingPrice(release.id, tempSellingPrice[release.id]);
                                                      }
                                                      setEditingSellingPrice(prev => ({ ...prev, [release.id]: false }));
                                                    }}
                                                  />
                                                </div>
                                              ) : actualSellingPrice ? (
                                                <div className="d-flex align-items-center gap-1">
                                                  <span className="fw-semibold text-success fs-12">
                                                    ${parseFloat(actualSellingPrice).toFixed(4)}
                                                  </span>
                                                  <button 
                                                    className="btn btn-sm p-0 border-0 text-primary"
                                                    onClick={() => {
                                                      setEditingSellingPrice(prev => ({ ...prev, [release.id]: true }));
                                                      setTempSellingPrice(prev => ({ ...prev, [release.id]: actualSellingPrice }));
                                                    }}
                                                    title="Edit selling price"
                                                    style={{ padding: '2px' }}
                                                  >
                                                    <i className="feather-edit-2 fs-11"></i>
                                                  </button>
                                                </div>
                                              ) : (
                                                <button
                                                  className="btn btn-sm btn-outline-success"
                                                  onClick={() => {
                                                    setEditingSellingPrice(prev => ({ ...prev, [release.id]: true }));
                                                    setTempSellingPrice(prev => ({ ...prev, [release.id]: '' }));
                                                  }}
                                                  title="Add selling price"
                                                >
                                                  <i className="feather-plus fs-11"></i>
                                                  Add
                                                </button>
                                              )}
                                            </div>
                                          </td>
                                          <td className="px-3 py-2 text-end">
                                            {actualSellingPrice ? (
                                              <span className="fw-medium text-success fs-12">
                                                ${soldActual.toFixed(2)}
                                              </span>
                                            ) : (
                                              <span className="fw-medium text-muted fs-12">-</span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                  })
                                ) : (
                                  <tr>
                                    <td colSpan="7" className="text-center py-5">
                                      <div className="text-muted">
                                        <i className="feather-calendar fs-1 mb-3 d-block"></i>
                                        <h5 className="text-muted">No releases found</h5>
                                        <p className="text-muted fs-12">Click "Add Release" to create a new release</p>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                                </>
                              )}
                            </>
                          ) : (
                        <div className="p-5 text-center">
                          <div className="text-muted">
                            <i className="feather-mouse-pointer fs-1 mb-3 d-block"></i>
                            <h5 className="text-muted">Select a Project</h5>
                            <p className="text-muted fs-12">Click on a project name in the investment table to view release details</p>
                          </div>
                        </div>
                      )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default InvestorDashboard;